// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { apiService } from "../../../services/api/apiService";
import { requireToken, shouldUseDemoData } from "../../../services/api/demoData";
import { noteDraftTenant } from "../../../utils/draftOwner";

import type {
  TenantSettingsModel,
  TenantSettingsPayload,
} from "../models/onboardingModels";

interface ApiEnvelope<T> {
  data?: T;
  error?: {
    message: string;
  };
}

interface TenantSettingsApiOptions {
  token?: string;
  allowTestModeRequests?: boolean;
}

interface TenantSettingsResponseDto {
  tenantId: string;
  settings: TenantSettingsPayload;
  updatedAtUtc: string;
  correlationId: string;
}

const SETTINGS_CACHE_TTL_MS = 30_000;

interface CachedSettingsEntry {
  value: TenantSettingsModel;
  expiresAt: number;
}

let stubTenantSettings: TenantSettingsModel = {
  tenantId: "tenant-current",
  settings: {
    onboarding: {
      bootstrapComplete: true,
    },
    tenant: {
      displayName: "Riddle Workspace",
    },
    business: {
      displayName: "Riddle Business",
    },
    workspace: {
      defaultTimeZone: "UTC",
    },
    communications: {
      multilingualEnabled: false,
    },
  },
  updatedAtUtc: new Date(0).toISOString(),
  correlationId: "local-stub",
};

const inflightTenantSettings = new Map<string, Promise<TenantSettingsModel>>();
const cachedTenantSettings = new Map<string, CachedSettingsEntry>();

function cacheTenantSettings(token: string, value: TenantSettingsModel): void {
  noteDraftTenant(value.tenantId);
  cachedTenantSettings.set(token, {
    value,
    expiresAt: Date.now() + SETTINGS_CACHE_TTL_MS,
  });
}

function readCachedTenantSettings(
  token: string,
  now: number,
): TenantSettingsModel | undefined {
  const cached = cachedTenantSettings.get(token);
  if (cached === undefined) {
    return undefined;
  }
  if (cached.expiresAt <= now) {
    cachedTenantSettings.delete(token);
    return undefined;
  }
  return cached.value;
}

export function __resetTenantSettingsCacheForTests(): void {
  inflightTenantSettings.clear();
  cachedTenantSettings.clear();
}

function unwrapApiEnvelope<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.error !== undefined) {
    throw new Error(envelope.error.message);
  }

  if (envelope.data === undefined) {
    throw new Error("API response was missing the expected data payload.");
  }

  return envelope.data;
}

function mergeTenantSettings(
  existing: TenantSettingsPayload,
  patch: Partial<TenantSettingsPayload>,
): TenantSettingsPayload {
  return {
    ...existing,
    ...(patch.onboarding
      ? { onboarding: { ...existing.onboarding, ...patch.onboarding } }
      : {}),
    ...(patch.tenant
      ? { tenant: { ...existing.tenant, ...patch.tenant } }
      : {}),
    ...(patch.business
      ? { business: { ...existing.business, ...patch.business } }
      : {}),
    ...(patch.workspace
      ? { workspace: { ...existing.workspace, ...patch.workspace } }
      : {}),
    ...(patch.communications
      ? { communications: { ...existing.communications, ...patch.communications } }
      : {}),
    ...(patch.subscription
      ? { subscription: { ...existing.subscription, ...patch.subscription } }
      : {}),
  };
}

export async function getTenantSettings(
  options?: TenantSettingsApiOptions & { refresh?: boolean; signal?: AbortSignal },
): Promise<TenantSettingsModel> {
  // Issue 910: the stub is only for test mode or an explicit demo build. With
  // no token this throws AuthTokenUnavailableError before any request.
  if (shouldUseDemoData(options)) {
    return stubTenantSettings;
  }

  const token = requireToken(options);
  const now = Date.now();

  if (!options?.refresh) {
    const cached = readCachedTenantSettings(token, now);
    if (cached !== undefined) {
      return cached;
    }
  }

  if (options?.signal !== undefined) {
    return requestTenantSettings(token, options.refresh, options.signal);
  }

  const inflight = inflightTenantSettings.get(token);
  if (inflight !== undefined) {
    return inflight;
  }

  const request = requestTenantSettings(token, options?.refresh);
  inflightTenantSettings.set(token, request);

  try {
    return await request;
  } finally {
    inflightTenantSettings.delete(token);
  }
}

async function requestTenantSettings(
  token: string,
  refresh: boolean | undefined,
  signal?: AbortSignal,
): Promise<TenantSettingsModel> {
  const response = await apiService.requestJson<
    ApiEnvelope<TenantSettingsResponseDto>
  >({
    path: "/api/onboarding/tenant-settings",
    method: "GET",
    token,
    ...(refresh ? { query: { refresh: true } } : {}),
    ...(signal !== undefined ? { signal } : {}),
  });

  const value = response.map(unwrapApiEnvelope).unwrap();
  cacheTenantSettings(token, value);
  return value;
}

export async function patchTenantSettings(
  patch: Partial<TenantSettingsPayload>,
  options?: TenantSettingsApiOptions,
): Promise<TenantSettingsModel> {
  // Issue 910: never fake a save without a token; the stub only changes in
  // test mode or an explicit demo build.
  if (shouldUseDemoData(options)) {
    stubTenantSettings = {
      ...stubTenantSettings,
      settings: mergeTenantSettings(stubTenantSettings.settings, patch),
      updatedAtUtc: new Date().toISOString(),
    };
    return stubTenantSettings;
  }

  const token = requireToken(options);

  const response = await apiService.requestJson<
    ApiEnvelope<TenantSettingsResponseDto>,
    { update: { mode: "partial"; settings: Partial<TenantSettingsPayload> } }
  >({
    path: "/api/onboarding/tenant-settings",
    method: "PATCH",
    token,
    body: {
      update: {
        mode: "partial",
        settings: patch,
      },
    },
  });

  const result = response.map(unwrapApiEnvelope).unwrap();

  cacheTenantSettings(token, result);

  return result;
}
